import React, {useState, useEffect} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
} from "@material-ui/core";
import {toast} from "react-toastify";
import axios from "axios";

export default function ParticipantEditDialog({open, onClose, participant, workshopId}) {
  const [form, setForm] = useState({
    prefix: "",
    fname: "",
    lname: "",
    school: "",
    email: "",
    tel: "",
  });

  const [errors, setErrors] = useState({});

  const API_URL = window?.location?.hostname?.includes("maroc")
    ? process.env.REACT_APP_API_MAROC
    : process.env.REACT_APP_API;

  useEffect(() => {
    if (participant) {
      setForm({
        prefix: participant.prefix || "",
        fname: participant.fname || "",
        lname: participant.lname || "",
        school: participant.school || "",
        email: participant.email || "",
        tel: participant.tel || "",
      });
    } else {
      setForm({
        prefix: "",
        fname: "",
        lname: "",
        school: "",
        email: "",
        tel: "",
      });
    }
    setErrors({});
  }, [participant, open]);

  const validate = () => {
    const newErrors = {};

    if (!form.fname.trim()) {
      newErrors.fname = "First name is required";
    }

    if (!form.lname.trim()) {
      newErrors.lname = "Last name is required";
    }

    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Invalid email format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    try {
      if (participant) {
        // Update existing participant
        await axios.put(`${API_URL}/workshops/${workshopId}/participants/${participant.id}`, form);
        toast.success("Participant updated successfully");
      } else {
        // Create new participant
        await axios.post(`${API_URL}/workshops/${workshopId}/participants`, form);
        toast.success("Participant added successfully");
      }
      onClose();
    } catch (err) {
      toast.error(participant ? "Failed to update participant" : "Failed to add participant");
    }
  };

  const handleChange = (field) => (e) => {
    setForm({...form, [field]: e.target.value});
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors({...errors, [field]: ""});
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" style={{fontWeight: 600}}>
          {participant ? "Edit Participant" : "Add New Participant"}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Prefix"
              fullWidth
              value={form.prefix}
              onChange={handleChange("prefix")}
              variant="outlined"
              placeholder="Mr./Ms./Dr."
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={4.5}>
            <TextField
              label="First Name"
              fullWidth
              required
              value={form.fname}
              onChange={handleChange("fname")}
              variant="outlined"
              error={!!errors.fname}
              helperText={errors.fname}
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={4.5}>
            <TextField
              label="Last Name"
              fullWidth
              required
              value={form.lname}
              onChange={handleChange("lname")}
              variant="outlined"
              error={!!errors.lname}
              helperText={errors.lname}
              size="small"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="School/Organization"
              fullWidth
              value={form.school}
              onChange={handleChange("school")}
              variant="outlined"
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Email"
              fullWidth
              required
              type="email"
              value={form.email}
              onChange={handleChange("email")}
              variant="outlined"
              error={!!errors.email}
              helperText={errors.email}
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Phone Number"
              fullWidth
              value={form.tel}
              onChange={handleChange("tel")}
              variant="outlined"
              size="small"
            />
          </Grid>
        </Grid>

        <Box mt={2} p={2} bgcolor="#f0f9ff" borderRadius={2}>
          <Typography variant="caption" color="textSecondary">
            <strong>Note:</strong> Make sure all information is correct before saving. The participant's name
            will appear on their certificate exactly as entered.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions style={{padding: 16}}>
        <Button onClick={onClose} style={{textTransform: "none"}}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSubmit}
          style={{textTransform: "none", fontWeight: 600}}
        >
          {participant ? "Update Participant" : "Add Participant"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
